import { useState, useEffect, useMemo, useCallback } from 'react';
import { Header } from './components/Header';
import { Map, type TerritoryBoundary } from './components/Map';
import { SatelliteBrowser } from './components/SatelliteBrowser';
import { TerritoryInfo, type TerritorySummary } from './components/TerritoryInfo';
import { generateGrid } from './utils/gridGenerator';
import { ARARIBOIA_CONFIG } from './data/reserveConfig';
import type { GridCell, LayerSettings, FUNAITerritoryEntry, FUNAITerritoryData, FUNAITerritoryProperties } from './types';
import './styles/global.css';

// Loaded territory data with boundary (array of rings for MultiPolygon support)
interface LoadedTerritory {
  entry: FUNAITerritoryEntry;
  boundary: [number, number][][];
  properties: FUNAITerritoryProperties;
}

// Helper to convert GeoJSON coordinates to Leaflet format [lat, lng]
// Returns array of rings to support both Polygon and MultiPolygon
function geoJsonToLeaflet(coordinates: number[][][] | number[][][][]): [number, number][][] {
  const toLatLng = (ring: number[][]): [number, number][] =>
    ring.map(coord => [coord[1], coord[0]] as [number, number]);

  if (Array.isArray(coordinates[0][0][0])) {
    // MultiPolygon - return outer ring of each polygon
    return (coordinates as number[][][][]).map(poly => toLatLng(poly[0]));
  }
  // Polygon - return outer ring as single-element array
  return [toLatLng((coordinates as number[][][])[0])];
}

// Calculate center from boundary
function calculateCenter(boundary: [number, number][]): [number, number] {
  const lats = boundary.map(p => p[0]);
  const lngs = boundary.map(p => p[1]);
  return [
    (Math.min(...lats) + Math.max(...lats)) / 2,
    (Math.min(...lngs) + Math.max(...lngs)) / 2
  ];
}

type ViewMode = 'map' | 'satellite';

function App() {
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [gridCells, setGridCells] = useState<GridCell[]>([]);
  const [isLoadingGrid, setIsLoadingGrid] = useState(true);
  const [layers, setLayers] = useState<LayerSettings>({
    showGrid: true,
    showBQIHeatmap: true,
    showBoundary: true,
    showBlockchain: false
  });

  // Territory selection state (multi-select)
  const [selectedTerritories, setSelectedTerritories] = useState<FUNAITerritoryEntry[]>([]);
  const [loadedTerritories, setLoadedTerritories] = useState<LoadedTerritory[]>([]);
  const [isTerritoryLoading, setIsTerritoryLoading] = useState(false);
  const [territoryKey, setTerritoryKey] = useState(0); // Force map re-render on territory change

  // Initialize grid
  useEffect(() => {
    console.log('🚀 Initializing Arariboia Biodiversity System...');

    // Generate grid cells
    const cells = generateGrid(ARARIBOIA_CONFIG.boundary, ARARIBOIA_CONFIG.center);
    setGridCells(cells);

    // DEBUG: Log first cell to verify coordinates
    if (cells.length > 0) {
      console.log('🔍 FIRST CELL DEBUG:', {
        id: cells[0].id,
        coordinates: cells[0].coordinates,
        bounds: cells[0].bounds,
        center: ARARIBOIA_CONFIG.center,
        boundarySample: ARARIBOIA_CONFIG.boundary.slice(0, 2)
      });
    }

    setIsLoadingGrid(false);
    console.log('✓ System initialized');
    console.log(`✓ ${cells.length} grid cells loaded`);
  }, []);

  // Handle territory selection (multi-select) - load ALL selected territories
  const handleTerritorySelect = useCallback(async (territories: FUNAITerritoryEntry[]) => {
    if (territories.length === 0) {
      // Reset to Arariboia default
      setSelectedTerritories([]);
      setLoadedTerritories([]);
      setTerritoryKey(prev => prev + 1);
      console.log('🗺️ Reset to Arariboia territory');
      return;
    }

    setIsTerritoryLoading(true);
    setSelectedTerritories(territories);

    try {
      // Load ALL selected territories in parallel
      const loadPromises = territories.map(async (territory): Promise<LoadedTerritory | null> => {
        try {
          const response = await fetch(`/data/funai/territories/${territory.filename}`);
          if (!response.ok) throw new Error(`Failed to load: ${response.status}`);

          const data: FUNAITerritoryData = await response.json();
          const feature = data.features[0];

          if (feature?.geometry) {
            return {
              entry: territory,
              boundary: geoJsonToLeaflet(feature.geometry.coordinates),
              properties: feature.properties
            };
          }
          return null;
        } catch (err) {
          console.error(`Failed to load territory ${territory.code}:`, err);
          return null;
        }
      });

      const results = await Promise.all(loadPromises);
      const loaded = results.filter((t): t is LoadedTerritory => t !== null);

      setLoadedTerritories(loaded);
      setTerritoryKey(prev => prev + 1);

      // Calculate total area
      const totalArea = loaded.reduce((sum, t) => sum + (t.properties.superficie_perimetro_ha || 0), 0);
      const totalPoints = loaded.reduce((sum, t) => sum + t.boundary.flat().length, 0);

      console.log(`🗺️ Loaded ${loaded.length} of ${territories.length} territories`, {
        totalAreaHa: totalArea.toLocaleString(),
        totalBoundaryPoints: totalPoints.toLocaleString()
      });
    } catch (error) {
      console.error('Failed to load territories:', error);
    } finally {
      setIsTerritoryLoading(false);
    }
  }, []);

  // Build boundaries array for Map component
  const territoryBoundaries = useMemo((): TerritoryBoundary[] => {
    if (loadedTerritories.length === 0) {
      // Default Arariboia boundary
      return [{
        id: 0,
        name: 'Arariboia Indigenous Reserve',
        boundary: [ARARIBOIA_CONFIG.boundary]
      }];
    }
    return loadedTerritories.map(t => ({
      id: t.entry.code,
      name: t.entry.name,
      boundary: t.boundary
    }));
  }, [loadedTerritories]);

  // Build summary for TerritoryInfo when multiple territories selected
  const territorySummary = useMemo((): TerritorySummary | undefined => {
    if (loadedTerritories.length === 0) return undefined;

    const uniqueStates = [...new Set(
      loadedTerritories
        .map(t => t.properties.uf_sigla)
        .filter((s): s is string => !!s)
        .flatMap(s => s.split(',').map(x => x.trim()))
    )];

    const uniquePhases = [...new Set(
      loadedTerritories
        .map(t => t.properties.fase_ti)
        .filter((p): p is string => !!p)
    )];

    return {
      count: loadedTerritories.length,
      totalAreaHa: loadedTerritories.reduce((sum, t) => sum + (t.properties.superficie_perimetro_ha || 0), 0),
      territories: loadedTerritories.map(t => ({
        code: t.entry.code,
        name: t.entry.name,
        areaHa: t.properties.superficie_perimetro_ha || 0,
        phase: t.properties.fase_ti,
        state: t.properties.uf_sigla
      })),
      uniqueStates,
      uniquePhases,
      totalBoundaryPoints: loadedTerritories.reduce((sum, t) => sum + t.boundary.flat().length, 0)
    };
  }, [loadedTerritories]);

  // Get center for map (use first loaded territory or Arariboia default)
  const mapCenter = useMemo((): [number, number] => {
    if (loadedTerritories.length === 0) return ARARIBOIA_CONFIG.center;
    // Calculate center from all boundaries combined
    const allPoints = loadedTerritories.flatMap(t => t.boundary.flat());
    return calculateCenter(allPoints);
  }, [loadedTerritories]);

  // Map click handler (for future features)
  const handleMapClick = useCallback((lat: number, lng: number) => {
    console.log(`Map clicked at [${lat.toFixed(6)}, ${lng.toFixed(6)}]`);
  }, []);

  // Calculate statistics
  const stats = useMemo(() => {
    const avgBQI = gridCells.length > 0
      ? gridCells.reduce((sum, cell) => sum + cell.metadata.bqiScore, 0) / gridCells.length
      : 0;

    return {
      totalCells: gridCells.length,
      avgBQI
    };
  }, [gridCells]);

  const handleToggleBoundary = useCallback(() => {
    setLayers(prev => ({
      ...prev,
      showBoundary: !prev.showBoundary
    }));
  }, []);

  // If satellite browser mode, render it with header
  if (viewMode === 'satellite') {
    return (
      <div className="app">
        <Header
          totalCells={stats.totalCells}
          avgBQI={stats.avgBQI}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          selectedTerritories={selectedTerritories}
          onTerritorySelect={handleTerritorySelect}
          isTerritoryLoading={isTerritoryLoading}
          showBoundary={layers.showBoundary}
          onToggleBoundary={handleToggleBoundary}
        />
        <div style={{ height: 'calc(100vh - 80px)' }}>
          <SatelliteBrowser apiUrl={`${window.location.protocol}//${window.location.hostname}:3004`} />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        totalCells={stats.totalCells}
        avgBQI={stats.avgBQI}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        selectedTerritories={selectedTerritories}
        onTerritorySelect={handleTerritorySelect}
        isTerritoryLoading={isTerritoryLoading}
        showBoundary={layers.showBoundary}
        onToggleBoundary={handleToggleBoundary}
      />

      <div className="main-content">
          {isLoadingGrid && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(26, 38, 66, 0.95)',
              color: 'white',
              padding: '32px 48px',
              borderRadius: '16px',
              zIndex: 2000,
              textAlign: 'center',
              minWidth: '320px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px' }}>
                Initializing System
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                Loading biodiversity grid...
              </div>
            </div>
          )}

          <Map
            key={territoryKey}
            center={mapCenter}
            boundaries={territoryBoundaries}
            gridCells={selectedTerritories.length > 0 ? [] : gridCells}
            layers={layers}
            onMapClick={handleMapClick}
            selectedTerritoryId={loadedTerritories.length > 0 ? loadedTerritories[0].entry.code : undefined}
          />

          {loadedTerritories.length > 0 && (
            <TerritoryInfo
              metadata={loadedTerritories.length === 1 ? loadedTerritories[0].properties : undefined}
              boundaryPoints={loadedTerritories.length === 1 ? loadedTerritories[0].boundary.flat().length : 0}
              boundary={loadedTerritories.length === 1 ? loadedTerritories[0].boundary : undefined}
              summary={territorySummary}
              onClose={() => handleTerritorySelect([])}
            />
          )}
      </div>
    </div>
  );
}

export default App;
